import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, User, Calendar, Mail, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FOCRequests = () => {
  const [pendingRequests, setpendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/api/users/pending-foc`, { headers });
      setpendingRequests(response.data);
    } catch (error) {
      console.error('Failed to load FOC requests:', error);
      toast.error('Failed to load FOC requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (studentId, studentName) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/users/${studentId}/approve-foc`, {}, { headers });
      toast.success(`FOC approved for ${studentName}`);
      loadPendingRequests(); // Reload list
    } catch (error) {
      console.error('Failed to approve FOC:', error);
      toast.error('Failed to approve FOC request');
    }
  };

  const handleReject = async (studentId, studentName) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/api/users/${studentId}/reject-foc`, {}, { headers });
      toast.success(`FOC rejected for ${studentName}, changed to monthly payment`);
      loadPendingRequests(); // Reload list
    } catch (error) {
      console.error('Failed to reject FOC:', error);
      toast.error('Failed to reject FOC request');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading FOC requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">FOC Approval Requests</h2>
          <p className="text-gray-600 mt-1">Review and approve Free of Charge student registrations</p>
        </div>
        <Badge variant="secondary" className="text-lg px-4 py-2">
          <Clock className="w-4 h-4 mr-2" />
          {pendingRequests.length} Pending
        </Badge>
      </div>

      {pendingRequests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Pending FOC Requests</h3>
            <p className="text-gray-600">All FOC requests have been reviewed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pendingRequests.map((student) => (
            <Card key={student.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-amber-500 rounded-full p-2">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{student.name}</CardTitle>
                      <Badge variant="warning" className="mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending Approval
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="truncate">{student.email}</span>
                  </div>
                  
                  {student.contact_number && (
                    <div className="flex items-center text-gray-600">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      <span>{student.contact_number}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                    <span>Requested: {new Date(student.created_at).toLocaleDateString()}</span>
                  </div>

                  {student.gk_code && (
                    <div className="bg-gray-50 rounded p-2 mt-2">
                      <p className="text-xs text-gray-500">GK Code</p>
                      <p className="font-semibold text-gray-900">{student.gk_code}</p>
                    </div>
                  )}

                  {student.group && (
                    <div className="bg-gray-50 rounded p-2">
                      <p className="text-xs text-gray-500">Group</p>
                      <p className="font-semibold text-gray-900 capitalize">
                        {student.group.replace('_', ' ')}
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t space-y-2">
                  <Button 
                    onClick={() => handleApprove(student.id, student.name)}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve FOC
                  </Button>
                  
                  <Button 
                    onClick={() => handleReject(student.id, student.name)}
                    variant="outline"
                    className="w-full text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject (Set to Monthly)
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FOCRequests;
